import Box from '@/common/components/Box'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import ResumeItem from '@/common/components/ResumeItem'
import { formatCurrency, formatUnit } from '@/common/utils/formatters'
import { MaintenanceDirectiveObjectiveData } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { WrenchIcon } from 'lucide-react'
import { useMemo } from 'react'

interface DiagnosticEntryObjectivePiecesProps {
  objective: MaintenanceDirectiveObjectiveData
  dataUpdatedAt?: number
}

const DiagnosticEntryObjectivePieces = ({ objective, dataUpdatedAt }: DiagnosticEntryObjectivePiecesProps) => {
  const piecesCountDisplay = useMemo(() => {
    const count = objective.diagnostic_pieces?.length || 0

    if (count === 0) {
      return 'Nenhuma peça'
    }

    if (count === 1) {
      return '1 Peça'
    }

    return `${count} Peças`
  }, [dataUpdatedAt])

  const totalValueSum = useMemo(() => {
    return (
      objective.diagnostic_pieces?.reduce((prev, curr) => {
        return prev + Number(curr.total_value)
      }, 0) || 0
    )
  }, [dataUpdatedAt])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <p className="font-bold text-base leading-none">Peças</p>

          <div className="flex items-center gap-0.5 text-xs leading-none">
            <span>{piecesCountDisplay}</span>

            <span>•</span>

            <span className="text-valid">{formatCurrency(totalValueSum)}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {objective.diagnostic_pieces?.map((diagnosticPiece) => (
          <Box key={diagnosticPiece.id} className="p-4">
            <div className="flex gap-4">
              <ImageCarousel
                files={diagnosticPiece.piece?.images?.map((image) => ({ id: image?.url, file: image?.url })) || []}
                Placeholder={
                  <ImageWithPlaceholder
                    className="size-28 rounded-md"
                    iconClassName="size-10"
                    placeholderIcon={WrenchIcon}
                  />
                }
                className="size-28 bg-[#fff]"
                imageClassName="object-contain"
                showExpandButton
                showRemoveButton={false}
                maxDots={5}
              />

              <div className="flex flex-col gap-2">
                <p className="text-base font-bold">{diagnosticPiece?.piece?.display_name || '--'}</p>

                <div className="flex gap-3">
                  <ResumeItem
                    className="text-sm"
                    label="Valor unt."
                    value={formatCurrency(Number(diagnosticPiece?.unit_price) || 0)}
                  />

                  <ResumeItem className="text-sm" label="Qntd" value={formatUnit(diagnosticPiece.quantity, 'unit')} />

                  <ResumeItem
                    className="text-sm"
                    label="Valor total"
                    value={formatCurrency(Number(diagnosticPiece?.total_value) || 0)}
                  />

                  <ResumeItem className="text-sm" label="Garantia" value={`${diagnosticPiece.warranty_in_days} Dias`} />

                  <ResumeItem
                    className="text-sm"
                    label="Código da peça"
                    value={diagnosticPiece?.piece?.number || '--'}
                  />

                  <ResumeItem
                    className="text-sm"
                    label="Fabricante"
                    value={diagnosticPiece?.piece?.brand?.display_name || '--'}
                  />
                </div>

                <ResumeItem className="text-sm" label="Descrição" value={diagnosticPiece.description} />
              </div>
            </div>
          </Box>
        ))}
      </div>
    </div>
  )
}

export default DiagnosticEntryObjectivePieces
